import React from 'react';

// Shared vocabulary for the College Desk essays screens (hub, detail, access,
// and the shared parent/advisor view). Everything here is presentation-only —
// the editorial theme lives in college-theme.css and is applied by
// `.college-scope`, which every one of those screens sits inside.

// ── Stage ────────────────────────────────────────────────────────────────
// The five-step stage sits apart from the "needs you" flag on purpose: an
// essay can be mid-polish and still be waiting on somebody.

export const STAGES = ['BRAINSTORMING', 'DRAFTING', 'REVISING', 'POLISHING', 'SUBMITTED'];

export const STAGE_LABELS = {
  BRAINSTORMING: 'Brainstorming',
  DRAFTING: 'Drafting',
  REVISING: 'Revising',
  POLISHING: 'Polishing',
  SUBMITTED: 'Submitted',
};

export const STAGE_LEGEND = ['Brainstorm', 'Draft', 'Revise', 'Polish', 'Submitted'];

// An essay counts as untouched when it is still at stage one with no words
// written and nothing said about it.
export const isNotStarted = (essay) =>
  essay.status === 'BRAINSTORMING' &&
  !essay.wordCount &&
  !(essay.brainstormNotes?.length) &&
  !(essay.tasks?.length);

export const stageIndex = (essay) => (isNotStarted(essay) ? -1 : STAGES.indexOf(essay.status));

export const stageLabel = (essay) => (isNotStarted(essay) ? 'Not started' : STAGE_LABELS[essay.status] || essay.status);

export function StageBar({ essay, showLabel = true }) {
  const filled = stageIndex(essay) + 1;
  const submitted = essay.status === 'SUBMITTED' && !isNotStarted(essay);
  const started = filled > 0;
  return (
    <div>
      <div style={{ display: 'flex', gap: 3 }}>
        {STAGES.map((_, i) => (
          <span
            key={i}
            style={{
              height: 4,
              flex: 1,
              background: i < filled ? 'var(--college-ink)' : 'var(--college-border)',
            }}
          />
        ))}
      </div>
      {showLabel && (
        <p
          style={{
            margin: '7px 0 0',
            fontSize: 11.5,
            fontWeight: 600,
            color: submitted
              ? 'var(--college-likely)'
              : started
              ? 'var(--college-ink)'
              : 'var(--college-muted-2)',
          }}
        >
          {stageLabel(essay)}
        </p>
      )}
    </div>
  );
}

// ── People ───────────────────────────────────────────────────────────────

export const initialsOf = (name) => {
  if (!name) return '??';
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '??';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const PARENT_COLORS = [
  { bg: 'var(--college-gold)', fg: 'var(--college-ink)' },
  { bg: 'var(--college-likely)', fg: 'var(--college-ink)' },
];

// Colour by role so the same person reads the same way on every screen: the
// student is ink, advisors are red, parents alternate gold/green.
export function personColor(person) {
  if (!person) return { bg: 'var(--college-border)', fg: 'var(--college-ink)' };
  if (person.role === 'STUDENT' || person.kind === 'user') {
    return { bg: 'var(--college-ink)', fg: 'var(--college-panel)' };
  }
  if (person.role === 'ADVISOR') return { bg: 'var(--college-red)', fg: 'var(--college-panel)' };
  // Stable pick per person, so a household of two parents doesn't collide.
  const seed = String(person.id || person.name || '')
    .split('')
    .reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  return PARENT_COLORS[seed % PARENT_COLORS.length];
}

export const ROLE_LABELS = { STUDENT: 'You', USER: 'Student', PARENT: 'Parent', ADVISOR: 'Advisor' };

export function Avatar({ person, size = 26, ring = false, title }) {
  const { bg, fg } = personColor(person);
  return (
    <span
      title={title || person?.name || ''}
      style={{
        width: size,
        height: size,
        borderRadius: 999,
        background: bg,
        color: fg,
        fontSize: Math.max(9, Math.round(size * 0.38)),
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        ...(ring ? { border: '2px solid var(--college-panel)' } : null),
      }}
    >
      {initialsOf(person?.name)}
    </span>
  );
}

export function AvatarStack({ people = [], size = 26, max = 3, align = 'flex-end' }) {
  const shown = people.slice(0, max);
  const extra = people.length - shown.length;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: align }}>
      {shown.map((person, i) => (
        <span key={`${person.kind}-${person.id}-${i}`} style={{ marginLeft: i === 0 ? 0 : -8 }}>
          <Avatar person={person} size={size} ring={i > 0 || shown.length > 1} />
        </span>
      ))}
      {extra > 0 && (
        <span style={{ marginLeft: 6, fontSize: 12, color: 'var(--college-ink-soft)' }}>+{extra}</span>
      )}
    </div>
  );
}

// Everybody who has touched an essay: whoever is writing it first, then note
// authors and task assignees in the order they turn up. `owner` is the student
// whatever the reader's own role is — an essay leads with its author, not with
// the person looking at it.
export function peopleOnEssay(essay, owner) {
  const seen = new Map();
  const add = (person) => {
    if (!person) return;
    const key = `${person.kind}:${person.id}`;
    if (!seen.has(key)) seen.set(key, person);
  };
  if (owner) add({ kind: 'user', id: owner.id, name: owner.name, role: 'STUDENT' });
  (essay.brainstormNotes || []).forEach((note) => add(note.author));
  (essay.tasks || []).forEach((task) => (task.assignees || []).forEach(add));
  return Array.from(seen.values());
}

// ── Essay taxonomy ───────────────────────────────────────────────────────

export const TYPE_LABELS = {
  COMMON_APP_PS: 'Personal statement',
  COMMON_APP_AI: 'Optional',
  SUPPLEMENTAL: 'Supplemental',
  UC_PIQ: 'UC PIQ',
  COALITION: 'Coalition',
  SCHOLARSHIP: 'Scholarship',
  OTHER: 'Other',
};

export const TYPE_FULL_LABELS = {
  COMMON_APP_PS: 'CommonApp Personal Statement',
  COMMON_APP_AI: 'CommonApp Additional Information',
  SUPPLEMENTAL: 'School Supplemental',
  UC_PIQ: 'UC Personal Insight',
  COALITION: 'Coalition Essay',
  SCHOLARSHIP: 'Scholarship Essay',
  OTHER: 'Other Essay',
};

export const PLATFORM_LABELS = {
  COMMON_APP: 'CommonApp',
  COALITION: 'Coalition',
  UC_APPLICATION: 'UC Application',
  QUESTBRIDGE: 'QuestBridge',
  OTHER: 'Other',
};

export const PLAN_SHORT = {
  ED: 'ED',
  ED_II: 'ED II',
  EA: 'EA',
  REA: 'REA',
  RD: 'RD',
  ROLLING: 'Rolling',
  UCAS: 'UCAS',
  OTHER: 'Other',
};

export const COMMON_APP_TYPES = ['COMMON_APP_PS', 'COMMON_APP_AI'];

// The school an essay belongs to, preferring the linked application so a
// renamed school stays in one group.
export const schoolOf = (essay) => essay.collegeApplication?.collegeName || essay.school || null;

export const essayTitle = (essay) =>
  essay.title || essay.school || essay.collegeApplication?.collegeName || TYPE_FULL_LABELS[essay.type] || 'Untitled essay';

// ── "Needs you" ──────────────────────────────────────────────────────────

export const WAITING_LABELS = {
  STUDENT: 'Needs you',
  ADVISOR: 'Needs advisor',
  PARENT: 'Needs parent',
};

export const openTaskCount = (essay) =>
  (essay.tasks || []).filter((t) => t.status !== 'DONE' && t.status !== 'DECLINED').length;

// An essay flags for attention when the student marked it as waiting on
// somebody, or when there's an open task on the reader's own plate.
export function needsAttention(essay, viewer) {
  if (essay.waitingOn && essay.waitingOn !== 'NONE') return true;
  return (essay.tasks || []).some(
    (task) =>
      task.status !== 'DONE' &&
      task.status !== 'DECLINED' &&
      (task.assignees || []).some((a) => a.kind === viewer?.kind && a.id === viewer?.id)
  );
}

// The flag reads from where the reader is standing: "needs you" only when it
// is genuinely their turn, and named otherwise. A parent looking at an essay
// waiting on the student should see whose turn it actually is.
export function waitingLabel(essay, viewer, ownerName) {
  const waiting = essay.waitingOn;
  if (waiting && waiting !== 'NONE') {
    const viewerRole = viewer?.kind === 'user' ? 'STUDENT' : viewer?.role;
    if (waiting === viewerRole) return 'Needs you';
    if (waiting === 'STUDENT') {
      const first = ownerName ? String(ownerName).trim().split(/\s+/)[0] : null;
      return first ? `Needs ${first}` : 'Needs them';
    }
    return WAITING_LABELS[waiting];
  }
  return needsAttention(essay, viewer) ? 'Needs you' : null;
}

// ── Formatting ───────────────────────────────────────────────────────────

const DAY = 24 * 60 * 60 * 1000;

export const dateShort = (iso) =>
  iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—';

export const dateLong = (iso) =>
  iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

export const daysUntil = (iso) => Math.max(0, Math.ceil((new Date(iso) - Date.now()) / DAY));

export function relativeDay(iso) {
  if (!iso) return '';
  const then = new Date(iso);
  const days = Math.floor((Date.now() - then) / DAY);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return then.toLocaleDateString('en-US', { weekday: 'short' });
  return then.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function lastEdited(iso) {
  const days = Math.floor((Date.now() - new Date(iso)) / DAY);
  if (days <= 0) return 'last edited today';
  if (days === 1) return 'last edited yesterday';
  return `last edited ${days} days ago`;
}

export const wordsLabel = (essay) =>
  `${essay.wordCount != null ? essay.wordCount : '—'} / ${essay.wordLimit != null ? essay.wordLimit : '—'} w`;

const NUMBER_WORDS = [
  'Zero', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
  'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen',
  'Nineteen', 'Twenty',
];

// "Fourteen essays · four submitted" — the eyebrow above the page title spells
// small numbers out, the way the rest of the Desk's editorial copy does.
export const spellNumber = (n, { capital = false } = {}) => {
  const word = NUMBER_WORDS[n];
  if (!word) return String(n);
  return capital ? word : word.toLowerCase();
};

// ── Small shared bits of chrome ──────────────────────────────────────────

export function Eyebrow({ children, style }) {
  return (
    <p
      style={{
        font: "600 11px/1 'Instrument Sans', sans-serif",
        letterSpacing: '0.16em',
        textTransform: 'uppercase',
        color: 'var(--college-muted-2)',
        margin: '0 0 10px',
        ...style,
      }}
    >
      {children}
    </p>
  );
}

export function NeedsFlag({ children }) {
  return (
    <span
      style={{
        padding: '3px 9px',
        background: 'var(--college-red)',
        color: 'var(--college-bg)',
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}

export function OutlineTag({ children }) {
  return (
    <span
      style={{
        padding: '2px 7px',
        border: '1px solid var(--college-border)',
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        color: 'var(--college-ink-soft)',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}

// The dark/ghost button pair used across the four screens. Rendered as real
// buttons so they're keyboard reachable, but styled flat like the design.
export function DeskButton({ variant = 'ghost', onClick, children, disabled, type = 'button', style }) {
  const dark = variant === 'dark';
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: dark ? '9px 16px' : '8px 14px',
        border: dark ? 'none' : '1px solid var(--college-border)',
        background: dark ? 'var(--college-ink)' : 'var(--college-panel)',
        color: dark ? 'var(--college-bg)' : 'var(--college-ink-soft)',
        fontFamily: 'inherit',
        fontSize: 12.5,
        fontWeight: dark ? 600 : 400,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {children}
    </button>
  );
}

// A flat text link/action in the desk's red, used for "Manage access →" and
// friends.
export function DeskLink({ onClick, children, style, as: As = 'button', ...rest }) {
  return (
    <As
      {...(As === 'button' ? { type: 'button', onClick } : { onClick })}
      {...rest}
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        font: 'inherit',
        fontSize: 12,
        color: 'var(--college-red)',
        cursor: 'pointer',
        textDecoration: 'none',
        ...style,
      }}
    >
      {children}
    </As>
  );
}

export function Toggle({ on, onChange, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={!!on}
      aria-label={label}
      onClick={() => onChange(!on)}
      style={{
        width: 40,
        height: 22,
        flexShrink: 0,
        borderRadius: 999,
        border: 'none',
        padding: '0 3px',
        background: on ? 'var(--college-ink)' : 'var(--college-border)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: on ? 'flex-end' : 'flex-start',
        cursor: 'pointer',
      }}
    >
      <span
        style={{
          width: 16,
          height: 16,
          borderRadius: 999,
          background: on ? 'var(--college-bg)' : 'var(--college-panel)',
          display: 'block',
        }}
      />
    </button>
  );
}

export function Checkbox({ checked, onChange, size = 15, label }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={!!checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      style={{
        width: size,
        height: size,
        flexShrink: 0,
        padding: 0,
        border: checked ? 'none' : '1.5px solid var(--college-ink)',
        background: checked ? 'var(--college-ink)' : 'transparent',
        color: 'var(--college-bg)',
        fontSize: Math.round(size * 0.66),
        lineHeight: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
      }}
    >
      {checked ? '✓' : ''}
    </button>
  );
}

// Panel/section heading: serif italic with a rule under it, the pattern every
// section on these screens uses.
export function SectionHead({ title, meta, right, size = 22 }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 10,
        borderBottom: '1px solid var(--college-ink)',
        paddingBottom: 8,
        flexWrap: 'wrap',
      }}
    >
      <h2
        className="font-serif-editorial"
        style={{ fontSize: size, fontStyle: 'italic', fontWeight: 400, margin: 0, color: 'var(--college-ink)' }}
      >
        {title}
      </h2>
      {meta && (
        <span className="font-mono-editorial" style={{ fontSize: 11, color: 'var(--college-muted)' }}>
          {meta}
        </span>
      )}
      {right && <span style={{ marginLeft: 'auto' }}>{right}</span>}
    </div>
  );
}

// Column template shared by the hub's essay rows and their header, so the
// CommonApp table and each school's table line up with one another.
export const ROW_COLUMNS = 'minmax(0,1fr) 132px 118px 96px 130px';

// ── Form chrome ──────────────────────────────────────────────────────────
// The shadcn dialog/inputs the rest of the app uses are styled by the School
// side's Tailwind theme and read as a foreign object inside the Desk, so the
// Desk's own screens use these instead.

export const inputStyle = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '9px 12px',
  border: '1px solid var(--college-border)',
  background: 'var(--college-panel)',
  color: 'var(--college-ink)',
  fontFamily: 'inherit',
  fontSize: 13.5,
  outline: 'none',
};

export function Field({ label, hint, children }) {
  return (
    <label style={{ display: 'block' }}>
      <span
        style={{
          display: 'block',
          font: "600 10px/1 'Instrument Sans', sans-serif",
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: 'var(--college-muted-2)',
          marginBottom: 7,
        }}
      >
        {label}
      </span>
      {children}
      {hint && (
        <span style={{ display: 'block', marginTop: 5, fontSize: 11.5, color: 'var(--college-muted)' }}>{hint}</span>
      )}
    </label>
  );
}

export function DeskModal({ title, eyebrow, onClose, children, width = 560 }) {
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(22,19,13,0.45)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '6vh 20px',
        overflowY: 'auto',
        zIndex: 60,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: width,
          background: 'var(--college-bg)',
          border: '1px solid var(--college-ink)',
          padding: '26px 28px 28px',
        }}
      >
        {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
        <h2
          className="font-serif-editorial"
          style={{ fontSize: 30, fontWeight: 400, margin: '0 0 20px', color: 'var(--college-ink)' }}
        >
          {title}
        </h2>
        {children}
      </div>
    </div>
  );
}

// "Priya", "Priya and David", "Priya, David and 2 others" — long lists stay
// one readable line.
function listNames(names, max = 2) {
  if (names.length <= max) {
    return names.length < 2 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  }
  const rest = names.length - max;
  return `${names.slice(0, max).join(', ')} and ${rest} other${rest === 1 ? '' : 's'}`;
}

// The one control that sends email. Nothing on the Desk mails anybody on its
// own — this box is unticked every time, and the action it sits on is what
// carries it. `who` names the people who would get it, so nobody sends into
// the dark.
export function NotifyToggle({ checked, onChange, who, disabled, style }) {
  const names = (who || []).filter(Boolean);
  const off = disabled || names.length === 0;
  const label = off ? 'Nobody to email' : `Email ${listNames(names)}`;
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 9,
        fontSize: 12.5,
        color: off ? 'var(--college-muted-2)' : 'var(--college-ink-soft)',
        cursor: off ? 'default' : 'pointer',
        ...style,
      }}
    >
      <input
        type="checkbox"
        checked={!!checked && !off}
        disabled={off}
        onChange={(e) => onChange(e.target.checked)}
        style={{ marginTop: 1, accentColor: '#16130D' }}
      />
      <span>
        {label}
        <span style={{ display: 'block', fontSize: 11, color: 'var(--college-muted)', marginTop: 2 }}>
          {off ? 'No email will be sent.' : 'Off unless you tick it — the Desk never emails on its own.'}
        </span>
      </span>
    </label>
  );
}

// Empty-state block in the Desk's voice: a rule, a serif line, a way forward.
export function DeskEmpty({ title, body, action }) {
  return (
    <div
      style={{
        border: '1px dashed var(--college-border)',
        background: 'var(--college-bg-alt)',
        padding: '44px 32px',
        textAlign: 'center',
      }}
    >
      <p
        className="font-serif-editorial"
        style={{ fontSize: 26, fontStyle: 'italic', margin: '0 0 10px', color: 'var(--college-ink)' }}
      >
        {title}
      </p>
      {body && (
        <p style={{ margin: '0 auto', maxWidth: 460, fontSize: 13.5, lineHeight: 1.6, color: 'var(--college-ink-soft)' }}>
          {body}
        </p>
      )}
      {action && <div style={{ marginTop: 18, display: 'flex', justifyContent: 'center' }}>{action}</div>}
    </div>
  );
}
